
import { PersistenceRegistry, ReducerRegistry } from '../base/redux';

import {
    ADD_CUSTOM_IMAGE,
    BACKGROUND_ENABLED,
    RESET_VIRTUAL_BACKGROUND,
    SET_VIRTUAL_BACKGROUND
} from './actionTypes';
import { VIRTUAL_BACKGROUND_TYPE } from './constants';

const STORE_NAME = 'features/virtual-background';

/**
 * Default state for virtual background feature.
 */
const DEFAULT_STATE = {
    autoCenterZoom: false,
    backgroundEffectEnabled: false,
    backgroundType: VIRTUAL_BACKGROUND_TYPE.NONE,
    blurValue: 0,
    selectedThumbnail: 'none',
    virtualSource: '',
    assetPath: '',
    customImages: []
};

/**
 * Sets up the persistence of the feature {@code virtual-background}.
 *
 * {@code customImages} is deliberately left out: the uploaded images are base64 data URLs of a few hundred KB each
 * and they are already persisted under the 'virtualBackgrounds' key (see persistCustomBackgrounds). Persisting them
 * here as well stored the same megabytes twice and was a large part of what exhausted the origin's localStorage
 * quota, which then broke persistence for every other feature.
 */
PersistenceRegistry.register(STORE_NAME, {
    assetPath: true,
    autoCenterZoom: true,
    backgroundEffectEnabled: true,
    backgroundType: true,
    blurValue: true,
    selectedThumbnail: true,
    virtualSource: true
}, DEFAULT_STATE);

/**
 * Reduces redux actions which activate/deactivate virtual background image, or
 * indicate if the virtual image background is activated/deactivated.
 *
 * @param {State} state - The current redux state.
 * @param {Action} action - The redux action to reduce.
 * @returns {State} The next redux state that is the result of reducing the specified action.
 */
ReducerRegistry.register(STORE_NAME, (state = DEFAULT_STATE, action) => {
    switch (action.type) {
    case SET_VIRTUAL_BACKGROUND: {
        return {
            ...state,
            virtualSource: action.virtualSource ?? state.virtualSource,
            blurValue: action.blurValue ?? state.blurValue,
            backgroundType: action.backgroundType ?? state.backgroundType,
            selectedThumbnail: action.selectedThumbnail ?? state.selectedThumbnail,
            backgroundEffectEnabled: action.backgroundEffectEnabled ?? state.backgroundEffectEnabled,
            assetPath: action.assetPath ?? state.assetPath,
            autoCenterZoom: action.autoCenterZoom ?? state.autoCenterZoom
        };
    }

    case BACKGROUND_ENABLED: {
        return {
            ...state,
            backgroundEffectEnabled: action.backgroundEffectEnabled
        };
    }

    case ADD_CUSTOM_IMAGE: {
        return {
            ...state,
            customImages: action.customImages ?? state.customImages
        };
    }

    case RESET_VIRTUAL_BACKGROUND: {
        return {
            ...DEFAULT_STATE,
            customImages: state.customImages // Preserve custom images on reset
        };
    }

    default:
        return state;
    }
});

/**
 * Selector to get the current virtual background options.
 *
 * @param {Object} state - The Redux state.
 * @returns {Object} The virtual background options.
 */
export function getVirtualBackgroundOptions(state: Object): Object {
    return state[STORE_NAME] || DEFAULT_STATE;
}

/**
 * Selector to check if virtual background effect is enabled.
 *
 * @param {Object} state - The Redux state.
 * @returns {boolean} Whether the background effect is enabled.
 */
export function isBackgroundEffectEnabled(state: Object): boolean {
    return state[STORE_NAME]?.backgroundEffectEnabled ?? false;
}

/**
 * Selector to get custom images.
 *
 * @param {Object} state - The Redux state.
 * @returns {Array} The custom images array.
 */
export function getCustomImages(state: Object): Array<Object> {
    return state[STORE_NAME]?.customImages ?? [];
}
